import {useState} from 'react';
import {DataTableSortStatus} from 'mantine-datatable';
// The comparator is SHARED (FNP-912) — nulls-last, numeric-vs-string and the
// empty-string rule live in one place rather than being re-derived per table.
import {sortRecords} from '@flexinet/ui-components';

import {
    useZonesFilter,
    useZonesInfiniteQuery,
    ZonesFilters,
} from './useZonesQuery';
import {useZonesSearch} from './useZonesSearch';
import {
    DelegationState,
    delegationState,
    DnsConnectionFailure,
    DnsZone,
} from '../types';

// The DEFAULT is a per-entity judgement and stays local; HOW two values compare
// does not (that is `sortRecords`).
//
// `name` rather than a timestamp: a DnsZone as this neutral layer returns it has
// NO created/updated field at all — it is fetched from the provider on every
// read, not stored here, so there is no such thing as "newest first". Of what
// remains, `name` is the only key that is both non-null (`String!`) and stable.
// Alphabetical is also what an operator scanning a portfolio expects.
export const DEFAULT_SORT_STATUS: DataTableSortStatus<DnsZone> = {
    columnAccessor: 'name',
    direction: 'asc',
};

export const matchesKeyword = (item: DnsZone, needle: string): boolean => {
    if (!needle) {
        return true;
    }
    const haystack = [
        item.name,
        item.provider,
        // ⚠️ `id` is deliberately NOT searchable. It is opaque — minted by the
        // adapter, meaningless to an operator, and not a string anyone has to
        // paste in. Making it a search key would train people to treat it as a
        // provider identifier, which is the one thing the schema forbids.
        ...item.nameservers,
    ];
    return haystack.some(value => typeof value === 'string' && value.toLowerCase()
                                                                    .includes(needle));
};

// ⚠️ Both branches return TRUE for an absent filter. An unset filter must not
// exclude anything, and in particular must not exclude the UNDETERMINED zones.
export const matchesFilters = (item: DnsZone, filters: ZonesFilters): boolean => {

    if (filters.provider && item.provider !== filters.provider) {
        return false;
    }

    if (filters.delegation) {
        // Compared through delegationState(), never against the raw boolean:
        // `item.appearsDelegated === false` and `== null` are DIFFERENT rows and
        // a filter that tests falsiness would return both for "not delegated".
        const state: DelegationState = delegationState(item);
        if (state !== filters.delegation) {
            return false;
        }
    }

    return true;
};

// ⚠️ FAILURES ARE DE-DUPLICATED ACROSS PAGES, KEYED ON THE CONNECTION.
//
// `failures` is per-PAGE: a connection that is down contributes an entry to
// every page fetched while it stays down. Flattening pages the way `items` are
// flattened would show the same broken connection three times after three
// "Load more" clicks — an alarm that grows by scrolling.
//
// The LAST occurrence wins, because it is the most recent read of that
// connection: a failure whose message changed between pages should surface the
// newer message, and one that started retryable and stopped being so should
// stop offering a retry.
export const dedupeFailures = (failures: DnsConnectionFailure[]): DnsConnectionFailure[] => {
    const byConnection = new Map<string, DnsConnectionFailure>();
    failures.forEach(failure => {
        if (failure && failure.connectionId) {
            byConnection.set(failure.connectionId, failure);
        }
    });
    return Array.from(byConnection.values());
};

export const useZones = (connectionId?: string) => {

    const query = useZonesInfiniteQuery(connectionId);

    // Component state, not the module-scoped store: sort order is how ONE
    // operator is looking at the table right now, unlike the keyword and the
    // filters, which are shared across sibling components (FNP-815).
    const [sortStatus, setSortStatus] =
        useState<DataTableSortStatus<DnsZone>>(DEFAULT_SORT_STATUS);

    const {keyword} = useZonesSearch();
    const {filters} = useZonesFilter();

    const pages = query.data?.pages ?? [];

    const items: DnsZone[] = pages.flatMap(
        page => (page && 'items' in page) ? page.items : [],
    ).filter(Boolean);

    // The `?? []` is not defensive noise — a page object that came back partial
    // would otherwise make this flatMap throw inside a render.
    const failures = dedupeFailures(
        pages.flatMap(page => (page && 'failures' in page) ? (page.failures ?? []) : []),
    );

    const needle = keyword.trim()
                          .toLowerCase();

    // ⚠️ Sorting, keyword and filters are CLIENT-SIDE, over the pages fetched so
    // far. None can reach rows the browser has not loaded. That is a property of
    // token pagination, not an oversight — and it is why the footer states a
    // LOADED count rather than a total.
    const visibleItems = sortRecords(
        items.filter(item => matchesKeyword(item, needle) && matchesFilters(item, filters)),
        sortStatus,
    );

    return {
        // The loaded set BEFORE keyword/filter narrowing — the window the
        // footer's "N of M loaded" claim is measured against.
        items,
        visibleItems,
        // ⚠️ SURFACED SEPARATELY FROM isError, ALWAYS, AND NEVER MERGED WITH IT.
        //
        // A tenant with three connections where one is down has real, complete
        // zones from the other two. Rendering an error state would hide them;
        // rendering nothing would make the missing ones look deleted. The table
        // shows what loaded AND the banner names what did not.
        failures,
        hasFailures: failures.length > 0,
        sortStatus,
        setSortStatus,
        loading: query.isLoading,
        // The QUERY failed — nothing is known. Distinct from `failures`, which
        // means the query answered and named the connections it could not read.
        isError: query.isError,
        isFetchingNextPage: query.isFetchingNextPage,
        nextToken: query.hasNextPage ? 'has-more' : null,
        fetchRecords: () => {
            if (query.hasNextPage && !query.isFetchingNextPage) {
                query.fetchNextPage();
            }
        },
        // Drives BOTH the error-state retry and the retryable-failure retry in
        // the banner. A failed connection is re-read by re-running the list —
        // there is no per-connection retry query on the schema, and inventing a
        // client-side one would be a call to a field that does not exist.
        reload: () => query.refetch(),
        isReloading: query.isRefetching,
    };
};
